import type Hls from '@adrise/hls.js';
import type { FPSDropData } from '@adrise/hls.js';
import type { AudioTrackInfo, QualityLevel } from '@adrise/player';
import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';

import { exposeToTubiGlobal } from 'client/global';
import { FREEZED_EMPTY_ARRAY } from 'common/constants/constants';
import { Experiment } from 'common/experiments/Experiment';
import type VideoResourceManager from 'common/features/playback/services/VideoResourceManager';
import type { FeatureSwitchLeaf } from 'common/services/FeatureSwitchManager';
import FeatureSwitchManager from 'common/services/FeatureSwitchManager';
import type StoreState from 'common/types/storeState';
import { formatBitrate, toFixed2 } from 'common/utils/format';

import styles from './PlaybackInfo.scss';

const POLL_INTERVAL = 1000;

export interface PlaybackInfoProps {
  className?: string;
  videoResourceManager?: VideoResourceManager;
  youboraExperimentMap?: {[key: string]: Experiment};
  showLevelDetails?: boolean;
  expandLevelDetails?: boolean;
  // keep this for the old player
  experimentName?: string;
  experimentTreatmentName?: string;
}

const FEATURE_SWITCH_PLAYER_RELATED_PATHS = [
  ['Ad', 'Availability'],
  ['Ad', 'MockUrl'],
  ['DRM', 'HDCP'],
  ['DRM', 'Fallback'],
  ['DRM', 'NativeCompatibility'],
  ['DRM', 'WidevineSecurityLevel'],
  ['Player', 'HlsVersion'],
  ['Player', 'DedicatedAdPlayer'],
  ['Logging', 'Player'],
  ['Player', 'Performance'],
  ['Player', 'VideoResourceType'],
  ['Player', 'HEVC'],
  ['Player', 'RESOLUTION'],
  ['Player', 'EnableHlsJS'],
];

// The Level[] type is not serializeable, so we transform it to this one
interface SerializeableLevels {
  attrs: {
    RESOLUTION: string;
    CODECS: string;
  }
}

interface PolledState {
  bufferedRange: [number, number][];
  levels: SerializeableLevels[] | undefined;
  currentLevel: number | undefined;
  fpsDropData: FPSDropData;
  audioTracks: AudioTrackInfo[] | undefined;
  bitrate: number;
  position: number;
  bufferPosition: number;
  timeGapToLastBuffer: number;
  SDKInfo: string;
  codecs: string | undefined;
  rendition: string | undefined;
  frameRate: string | undefined | number;
  qualityLevel: QualityLevel;
  preloaded: string;
  webWorkerFragment: React.ReactNode | null;
  experiments: Experiment<unknown, string>[];
  videoResources: ReturnType<VideoResourceManager['getAllVideoResources']>;
  currentVideoResource: ReturnType<VideoResourceManager['getCurrentResource']>;
  showPlaybackInfo?: boolean;
}
const emptyFpsDropData: FPSDropData = { currentDropped: 0, currentDecoded: 0, totalDroppedFrames: 0 };

const emptyQualityLevel: QualityLevel = { label: '', bitrate: 0, width: 0, height: 0 };

export const PlaybackInfo: FunctionComponent<PlaybackInfoProps> = ({
  videoResourceManager,
  youboraExperimentMap,
  experimentName,
  experimentTreatmentName,
  className,
  showLevelDetails = false,
  expandLevelDetails = false,
}) => {
  const store = useStore();
  const [showPlaybackInfo, setShowPlaybackInfo] = useState(true);
  const timerRef = useRef<number>();
  const [polledState, setPolledState] = useState<PolledState>({
    bufferedRange: FREEZED_EMPTY_ARRAY,
    levels: undefined,
    currentLevel: 0,
    fpsDropData: emptyFpsDropData,
    showPlaybackInfo: true,
    audioTracks: undefined,
    bitrate: 0,
    position: 0,
    bufferPosition: 0,
    timeGapToLastBuffer: 0,
    SDKInfo: '',
    codecs: '',
    rendition: '',
    frameRate: 0,
    qualityLevel: emptyQualityLevel,
    preloaded: '',
    webWorkerFragment: null,
    experiments: [],
    videoResources: [],
    currentVideoResource: undefined,
  });
  const prevPolledStateStringRef = useRef<string>(JSON.stringify(polledState));

  const featureSwitchSetting = FEATURE_SWITCH_PLAYER_RELATED_PATHS
    .filter(path => !FeatureSwitchManager.isDefault(path))
    .map(path => {
      const config = FeatureSwitchManager.getConfig(path) as FeatureSwitchLeaf;
      const value = FeatureSwitchManager.get(path);
      const optionValue = config.options.filter(option => option.value === value)[0];
      const title = optionValue ? optionValue.title : /* istanbul ignore next */ undefined;
      return {
        key: config.title,
        value: title,
      };
    });

  useEffect(() => {
    timerRef.current = window.setInterval(() => {

      /**
       * BUFFERED RANGE
       */
      /* istanbul ignore next */
      const playerBufferedRange = window.Tubi?.player?.getBufferedRange?.() || [];
      const bufferedRange: [number, number][] = (playerBufferedRange.length ? playerBufferedRange.map((range: [number, number]) => {
        // limit to 2 decimal places
        return [toFixed2(range[0]), toFixed2(range[1])];
      }) : FREEZED_EMPTY_ARRAY);

      /**
       * LEVEL/ CURRENTLEVEL
       */
      let levels: SerializeableLevels[] | undefined;
      let currentLevel: number | undefined;
      if (showLevelDetails) {
        /* istanbul ignore next */
        const hls = (window.Tubi?.player?.adapter?.extension?.hls || window.Tubi?.player?.adapter?.player) as Hls;
        if (hls) {
          levels = hls.levels.map((level) => {
            return {
              attrs: {
                RESOLUTION: level.attrs.RESOLUTION ?? '',
                CODECS: level.attrs.CODECS ?? '',
              },
            };
          });
          currentLevel = hls.currentLevel;
        }
      }

      /**
       * FPS DROP DATA
       */
      /* istanbul ignore next */
      const fpsDropData = window.Tubi?.player?.adapter?.extension?.getFPSDropData?.() ?? emptyFpsDropData;

      /**
       * AUDIO TRACKS
       */
      const audioTracks = window.Tubi?.player?.getAudioTracks?.();

      /**
       * SDK INFORMATION
       */
      const SDKName = window.Tubi?.player?.SDKName || 'unknown';
      /* istanbul ignore next */
      const sdkVersion = window.Tubi?.player?.adapter?.extension?.Hls?.version
        /* istanbul ignore next */
        || window.Tubi?.player?.adapter?.Hls?.version
        /* istanbul ignore next */
        || window.videometrics?.frameworkVersion
        /* istanbul ignore next */
        || window.webapis?.avplay?.getVersion?.();
      /* istanbul ignore next */
      const SDKInfo = sdkVersion ? `${SDKName}-${sdkVersion}` : SDKName;

      /**
       * EXPERIMENTS
       */
      /* istanbul ignore next */
      const experiments = Object.values(youboraExperimentMap ?? {}).filter(experiment => {
        if (experiment instanceof Experiment) {
          return experiment.isInExperiment();
        }
        return false;
      });

      /**
       * VIDEO RESOURCE MANAGER
       */
      const videoResources = videoResourceManager?.getAllVideoResources() || FREEZED_EMPTY_ARRAY;
      const currentVideoResource = videoResourceManager?.getCurrentResource();

      /**
       * REDUX STORE POLLING
       */
      const { player: { bitrate, progress: { position, bufferPosition }, timeGapToLastBuffer } } = store.getState() as StoreState;

      const newPolledState = {
        bufferedRange,
        levels,
        currentLevel,
        fpsDropData,
        audioTracks,
        bitrate,
        position,
        bufferPosition,
        timeGapToLastBuffer,
        SDKInfo,
        codecs: window.Tubi?.player?.getCodecs?.(),
        rendition: window.Tubi?.player?.getRendition?.(),
        frameRate: window.Tubi?.player?.getFrameRate?.(),
        qualityLevel: window.Tubi?.player?.getQualityLevel?.() ?? emptyQualityLevel,
        preloaded: window.Tubi?.player?.adapter?.extension?.isPreloaded ? 'Yes' : 'No',
        webWorkerFragment: window.Tubi?.player?.getIsUsingWebWorker?.() ? <p>Using Hls.js Web Worker: Yes</p> : null,
        experiments,
        videoResources,
        currentVideoResource,
      };

      const newPolledStateString = JSON.stringify(newPolledState);
      if (prevPolledStateStringRef.current !== newPolledStateString) {
        prevPolledStateStringRef.current = newPolledStateString;
        setPolledState(newPolledState);
      }
    }, POLL_INTERVAL);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [showLevelDetails, store, youboraExperimentMap, videoResourceManager]);

  /**
   * Expose the togglePlaybackInfo function to the global scope
   */
  useEffect(() => {
    /* istanbul ignore next */
    const togglePlaybackInfo = () => {
      setShowPlaybackInfo(!showPlaybackInfo);
    };
    exposeToTubiGlobal({ togglePlaybackInfo });
    return () => {
      exposeToTubiGlobal({ togglePlaybackInfo: undefined });
    };
  });

  const { bufferedRange, levels, currentLevel, fpsDropData, audioTracks, bitrate, bufferPosition, position, timeGapToLastBuffer, SDKInfo, codecs, rendition, frameRate, qualityLevel, preloaded, webWorkerFragment, experiments, videoResources, currentVideoResource } = polledState;

  return <div className={classNames(styles.PlaybackInfoWrapper, className, { [styles.invisible]: !showPlaybackInfo })}>
    <h3>Player Information</h3>
    <p>SDK: {SDKInfo}</p>
    <p>current level: {`${codecs}  ${rendition} ${frameRate}`}</p>
    <p>Quality: {qualityLevel ? `${qualityLevel.label}` : 'N/A'}</p>
    <p>Bitrate: {formatBitrate(bitrate)}/s</p>
    <p>Remain Buffer: {(bufferPosition - position).toFixed(2)}s</p>
    <p>Preloaded: {preloaded}</p>
    {timeGapToLastBuffer > 0 ? <p>Gap to the Last Buffer: {(timeGapToLastBuffer / 1000).toFixed(2)}s</p> : null}
    {bufferedRange.length ? <p>Buffered: {JSON.stringify(bufferedRange).slice(1, -1)}</p> : null}
    {fpsDropData.currentDecoded > 0
      ? <p>FPS Drop: {(fpsDropData.currentDropped * 100 / fpsDropData.currentDecoded).toFixed(1)}% ({fpsDropData.currentDropped} / {fpsDropData.currentDecoded})</p>
      : null}
    {webWorkerFragment}
    {
      experiments.map(experiment => <p key={experiment.id}>{experiment.experimentName}:{experiment.treatment}</p>)
    }
    {experiments.length ? <div className={styles.dividingLine} /> : null}
    {experimentName ? <p>Experiment: {experimentName}</p> : null}
    {experimentTreatmentName ? <p>Group: {experimentTreatmentName}</p> : null}
    {experimentTreatmentName ? <div className={styles.dividingLine} /> : null}
    {
      audioTracks && audioTracks.length
        ? <Fragment>
          <h3>Audio Tracks</h3>
          <ol>
            {audioTracks.map(track => {
              const { active, id, role, language } = track;
              return <li className={active ? styles.currentLevel : ''} key={id}>{`${language}-${role}`}</li>;
            })}
          </ol>
        </Fragment>
        : null
    }
    <h3>Video Resource Type</h3>
    <ol>
      {
        videoResources?.map((resource, index) => {
          /* istanbul ignore next */
          const hdcpValue = resource.license_server?.hdcp_version ?? '';
          const { manifest: { url } } = resource;
          /* istanbul ignore next */
          const cdn = url.match(/https?:\/\/([^/.]*)/)?.[1] ?? 'unknown';
          const resolutionStr = `${resource.codec}_${resource.resolution}`;
          return <li className={resource === currentVideoResource ? styles.currentLevel : ''} key={`${resource.type}${index}`}>
            <p>{resolutionStr} {resource.type} {hdcpValue} {cdn}</p>
          </li>;
        })
      }
    </ol>
    {
      levels
        ? (
          <Fragment>
            <h3>Level Details</h3>
            {expandLevelDetails ? (
              <ol>
                {levels.map((level, index) => {
                  return <li className={currentLevel === index ? styles.currentLevel : ''} key={`${level.attrs.RESOLUTION}-${level.attrs.CODECS}`}>
                    <p>resolution: {level.attrs.RESOLUTION}</p>
                    <p>codecs: {level.attrs.CODECS}</p>
                  </li>;
                })}
              </ol>
            ) : (
              <ol>
                {levels.filter((item, index) => index === currentLevel).map((level) => {
                  return <li className={styles.currentLevel} key={`${level.attrs.RESOLUTION}-${level.attrs.CODECS}`}>
                    <p>resolution: {level.attrs.RESOLUTION}</p>
                    <p>codecs: {level.attrs.CODECS}</p>
                  </li>;
                })}
              </ol>
            )}
          </Fragment>
        )
        : null
    }
    {featureSwitchSetting.length ? <div className={styles.dividingLine} /> : null}
    {featureSwitchSetting.length ? <h3>Feature Switch</h3> : null}
    {featureSwitchSetting.map(({ key, value }) => <p key={key}>{key}: {value}</p>)}
  </div>;
};

export default PlaybackInfo;
